// src/useAdminNotifications.js - everything waiting on an admin, in one place
import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';

/**
 * The single source for "what needs attention".
 *
 * Extracted from the header bell so the dashboard card can show the same list.
 * Two components each running their own queries would drift the moment either
 * gained a source, and would disagree about the count, which is exactly the
 * kind of thing that makes someone stop trusting the number.
 *
 * Notifications are derived from the tables that already hold the truth rather
 * than copied into a table of their own, so nothing can fall out of step with
 * them. Only the fact that a given admin has seen a given item is stored, in
 * admin_notification_reads, keyed "<source>:<row id>".
 */
export default function useAdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [adminEmail, setAdminEmail] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const email = (sessionData?.session?.user?.email || '').toLowerCase();
      setAdminEmail(email);

      const [errors, bookings, payouts, contact, reports, reads] = await Promise.all([
        supabase
          .from('system_errors')
          .select('*')
          .eq('resolved', false)
          .order('created_at', { ascending: false })
          .limit(10),
        supabase
          .from('bookings')
          .select('*')
          .eq('status', 'Pending')
          .order('created_at', { ascending: false })
          .limit(5),
        supabase
          .from('payout_requests')
          .select('*')
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
          .limit(5),
        // Table name really does have a space in it.
        supabase
          .from('Contact Us')
          .select('*')
          .in('status', ['Open', 'Pending'])
          .order('created_at', { ascending: false })
          .limit(10),
        supabase
          .from('content_reports')
          .select('*')
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
          .limit(10),
        email
          ? supabase
              .from('admin_notification_reads')
              .select('notification_key')
              .eq('admin_email', email)
          : Promise.resolve({ data: [] }),
      ]);

      const readKeys = new Set((reads.data || []).map(r => r.notification_key));
      const all = [];
      const add = (key, type, title, message, timestamp, action) => {
        all.push({ id: key, type, title, message, timestamp, read: readKeys.has(key), action });
      };

      // Reports first: someone reported another user, which carries a safety
      // obligation the other sources do not.
      reports.data?.forEach(r =>
        add(
          `report:${r.id}`,
          'error',
          'User reported',
          `${r.report_type || 'Report'} on ${r.content_type || 'content'}${r.description ? `: ${r.description}` : ''}`,
          r.created_at,
          '/dashboard/reports-queue',
        ),
      );

      errors.data?.forEach(e =>
        add(
          `issue:${e.id}`,
          e.severity === 'critical' || e.severity === 'error' ? 'error' : 'warning',
          e.title || 'System Error',
          e.message,
          e.created_at,
          '/dashboard/issues',
        ),
      );

      contact.data?.forEach(c =>
        add(
          `contact:${c.id}`,
          c.priority === 'High' ? 'warning' : 'info',
          `Support: ${c.subject || 'New message'}`,
          `${c.name || 'Someone'}${c.email ? ` (${c.email})` : ''}${c.about ? ` — ${c.about}` : ''}`,
          c.created_at,
          '/dashboard/contact-us',
        ),
      );

      payouts.data?.forEach(p =>
        add(
          `payout:${p.id}`,
          'info',
          'Pending Payout',
          `Payout request of $${p.amount || p.total_amount} pending approval`,
          p.created_at,
          '/dashboard/payouts',
        ),
      );

      bookings.data?.forEach(b =>
        add(
          `booking:${b.id}`,
          'info',
          'New Booking Request',
          `New booking request from ${b.client_id || 'client'}`,
          b.created_at,
          '/dashboard/bookings',
        ),
      );

      all.sort((a, b) => {
        if (a.read !== b.read) return a.read ? 1 : -1;
        return new Date(b.timestamp) - new Date(a.timestamp);
      });

      setNotifications(all);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // The screen updates whether or not the write lands, because a failed insert
  // should not leave the badge stuck on a number already dealt with. Without
  // the insert the mark only lasts until the next refresh, which was the
  // original bug.
  const persistReads = useCallback(
    async (keys) => {
      if (!adminEmail || keys.length === 0) return;
      const { error } = await supabase
        .from('admin_notification_reads')
        .upsert(
          keys.map(k => ({ admin_email: adminEmail, notification_key: k })),
          { onConflict: 'admin_email,notification_key', ignoreDuplicates: true },
        );
      if (error) console.error('Could not save notification read state:', error);
    },
    [adminEmail],
  );

  const markAsRead = useCallback(
    async (id) => {
      setNotifications(prev => prev.map(n => (n.id === id ? { ...n, read: true } : n)));
      await persistReads([id]);
    },
    [persistReads],
  );

  const markAllAsRead = useCallback(async () => {
    const unread = notifications.filter(n => !n.read).map(n => n.id);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    await persistReads(unread);
  }, [notifications, persistReads]);

  const unread = notifications.filter(n => !n.read);

  return {
    notifications,
    unread,
    unreadCount: unread.length,
    loading,
    markAsRead,
    markAllAsRead,
    refresh: fetchNotifications,
    addLive: (n) => setNotifications(prev => (prev.some(p => p.id === n.id) ? prev : [n, ...prev])),
  };
}
